import { callConfig } from '../configs/call';
import { api } from './api';

const FALLBACK_CALLABLE_USERS = [
    {
        userId: 'd1',
        displayName: 'Dr. Ana',
        role: 'doctor'
    },
    {
        userId: 'd2',
        displayName: 'Dr. Joao',
        role: 'doctor'
    },
    {
        userId: 'c1',
        displayName: 'Ana',
        role: 'caregiver'
    },
    {
        userId: 'c2',
        displayName: 'Joao',
        role: 'caregiver'
    }
];

const makeRoleHeaders = (userRoleUuid) => {
    if (!userRoleUuid || typeof userRoleUuid !== 'string') {
        throw new Error('Missing active user role UUID for call request.');
    }

    return {
        'USER-ROLE': userRoleUuid
    };
};

const normalizeTurnConfig = (data) => {
    if (!data || !Array.isArray(data.iceServers) || data.iceServers.length === 0) {
        return null;
    }

    return {
        iceServers: data.iceServers,
        ...(typeof data.ttlSec === 'number' ? { ttlSec: data.ttlSec } : {})
    };
};

const ONLINE_WINDOW_MS = 120 * 1000;

const parseIsoTime = (value) => {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }

    const ts = Date.parse(value);
    return Number.isNaN(ts) ? null : ts;
};

const normalizeUsersEndpointCallableUsers = (data, currentUserId, includeCurrentUser = false) => {
    if (!Array.isArray(data)) {
        return null;
    }

    const now = Date.now();

    const mapped = data
        .filter(
            (item) =>
                item &&
                (typeof item.uuid === 'string' ||
                    typeof item.role_uuid === 'string' ||
                    typeof item.user_uuid === 'string')
        )
        .map((item) => ({
            userId:
                typeof item.uuid === 'string' && item.uuid
                    ? item.uuid
                    : typeof item.role_uuid === 'string' && item.role_uuid
                      ? item.role_uuid
                      : item.user_uuid,
            displayName:
                typeof item.name === 'string' && item.name.trim()
                    ? item.name.trim()
                    : typeof item.email === 'string' && item.email.trim()
                      ? item.email.trim()
                      : typeof item.uuid === 'string' && item.uuid
                        ? item.uuid
                        : typeof item.role_uuid === 'string' && item.role_uuid
                          ? item.role_uuid
                          : item.user_uuid,
            ...(typeof item.role === 'string' ? { role: item.role } : {}),
            ...(typeof item.clinician_type === 'string'
                ? { clinicianType: item.clinician_type }
                : {}),
            lastCallPing: typeof item.last_call_ping === 'string' ? item.last_call_ping : null,
            isOnline: (() => {
                const lastPingTs = parseIsoTime(item.last_call_ping);
                if (!lastPingTs) {
                    return false;
                }
                return now - lastPingTs <= ONLINE_WINDOW_MS;
            })()
        }));

    if (includeCurrentUser) {
        return mapped;
    }

    const filtered = currentUserId
        ? mapped.filter((item) => item.userId !== currentUserId)
        : mapped;

    return filtered;
};

const mapLocalDevUsers = (currentUserId, includeCurrentUser = false) => {
    const users = [
        {
            userId: 'user-a',
            displayName: 'User A',
            role: 'agent',
            isOnline: true
        },
        {
            userId: 'user-b',
            displayName: 'User B',
            role: 'agent',
            isOnline: true
        }
    ];

    if (!currentUserId || includeCurrentUser) {
        return users;
    }

    const filtered = users.filter((user) => user.userId !== currentUserId);
    return filtered.length > 0 ? filtered : users;
};

const searchLocalDevUsers = (query, currentUserId, includeCurrentUser = false) => {
    const normalizedQuery = String(query || '')
        .trim()
        .toLowerCase();

    const baseUsers = mapLocalDevUsers(currentUserId, includeCurrentUser);

    if (!normalizedQuery) {
        return baseUsers;
    }

    return baseUsers.filter((user) =>
        String(user.displayName || '')
            .toLowerCase()
            .includes(normalizedQuery)
    );
};

const buildCallableUsersHeaders = ({ userRoleUuid, roleFilters, paginationToken, paginationLimit }) => {
    const headers = makeRoleHeaders(userRoleUuid);

    if (Array.isArray(roleFilters) && roleFilters.length > 0) {
        headers['CVP-QUERY-ROLE'] = JSON.stringify(roleFilters);
    }

    if (paginationToken) {
        headers['PAGINATION-TOKEN'] = paginationToken;
    }

    if (paginationLimit) {
        headers['PAGINATION-LIMIT'] = String(paginationLimit);
    }

    return headers;
};

const getLocalDevSignalingToken = async (userId) => {
    const response = await fetch(
        `${callConfig.devAuthBaseUrl}/dev/signaling-jwt?userId=${encodeURIComponent(userId)}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json'
            }
        }
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch local signaling token: ${response.status} ${response.statusText}`
        );
    }

    const data = await response.json();
    const token = data?.token;

    if (!token || typeof token !== 'string') {
        throw new Error('Invalid local signaling token response.');
    }

    return { token };
};

const getLocalDevTurnConfig = async (userId) => {
    const response = await fetch(
        `${callConfig.devAuthBaseUrl}/dev/turn-credentials?userId=${encodeURIComponent(userId)}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json'
            }
        }
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch local TURN credentials: ${response.status} ${response.statusText}`
        );
    }

    const data = await response.json();
    const normalized = normalizeTurnConfig(data);

    if (!normalized) {
        throw new Error('Invalid local TURN response. Missing iceServers.');
    }

    return normalized;
};

export const getSignalingToken = async ({ userRoleUuid, userId } = {}) => {
    if (callConfig.mode === 'local-dev') {
        return await getLocalDevSignalingToken(userId || callConfig.devUserId);
    }

    const response = await api.get(callConfig.signalingTokenPath, {
        headers: makeRoleHeaders(userRoleUuid)
    });

    const token = response?.data?.token;

    if (!token || typeof token !== 'string') {
        throw new Error('Invalid signaling token response.');
    }

    return {
        token
    };
};

export const getTurnConfig = async ({ userRoleUuid, userId } = {}) => {
    if (callConfig.mode === 'local-dev') {
        try {
            return await getLocalDevTurnConfig(userId || callConfig.devUserId);
        } catch (error) {
            if (callConfig.turnFallbackEnabled) {
                return {
                    iceServers: callConfig.turnFallbackIceServers
                };
            }

            throw error;
        }
    }

    try {
        const response = await api.get(callConfig.turnTokenPath, {
            headers: makeRoleHeaders(userRoleUuid)
        });

        const normalized = normalizeTurnConfig(response?.data);
        if (normalized) {
            return normalized;
        }

        throw new Error('TURN response does not include a valid iceServers array.');
    } catch (error) {
        if (callConfig.turnFallbackEnabled) {
            return {
                iceServers: callConfig.turnFallbackIceServers
            };
        }

        throw error;
    }
};

export const getCallableUsers = async ({
    currentUserId,
    userRoleUuid,
    roleFilters,
    paginationToken,
    paginationLimit,
    includeCurrentUser = false
} = {}) => {
    if (callConfig.mode === 'local-dev') {
        return {
            users: mapLocalDevUsers(currentUserId || callConfig.devUserId, includeCurrentUser),
            nextToken: null
        };
    }

    try {
        const response = await api.get(callConfig.callableUsersPath, {
            headers: buildCallableUsersHeaders({
                userRoleUuid,
                roleFilters,
                paginationToken,
                paginationLimit
            })
        });
        const normalized = normalizeUsersEndpointCallableUsers(response?.data, currentUserId, includeCurrentUser);

        if (normalized) {
            return {
                users: normalized,
                nextToken: response?.headers?.['pagination-token'] || null,
                totalCount: Number(response?.headers?.['pagination-count'] || 0)
            };
        }

        throw new Error('Users response is invalid.');
    } catch (error) {
        // Temporary fallback while /calls/users is unstable:
        // use /users with the same role filters so pages keep working.
        try {
            const fallbackResp = await api.get('/users', {
                headers: buildCallableUsersHeaders({
                    userRoleUuid,
                    roleFilters,
                    paginationToken,
                    paginationLimit
                })
            });
            const normalized = normalizeUsersEndpointCallableUsers(
                fallbackResp?.data,
                currentUserId,
                includeCurrentUser
            );

            if (normalized) {
                return {
                    users: normalized,
                    nextToken: fallbackResp?.headers?.['pagination-token'] || null,
                    totalCount: Number(fallbackResp?.headers?.['pagination-count'] || 0)
                };
            }
        } catch (fallbackError) {
            // Continue to configured local fallback below.
        }

        if (callConfig.callableUsersFallbackEnabled) {
            return {
                users: FALLBACK_CALLABLE_USERS,
                nextToken: null,
                totalCount: FALLBACK_CALLABLE_USERS.length
            };
        }

        throw error;
    }
};

export const searchCallableUsers = async ({
    query,
    currentUserId,
    userRoleUuid,
    roleFilters,
    paginationToken,
    paginationLimit,
    includeCurrentUser = false
} = {}) => {
    if (callConfig.mode === 'local-dev') {
        const users = searchLocalDevUsers(query, currentUserId || callConfig.devUserId, includeCurrentUser);
        return {
            users,
            nextToken: null,
            totalCount: users.length
        };
    }

    const response = await api.post(
        '/users/search',
        {
            query: String(query || '')
        },
        {
            headers: buildCallableUsersHeaders({
                userRoleUuid,
                roleFilters,
                paginationToken,
                paginationLimit
            })
        }
    );

    const normalized = normalizeUsersEndpointCallableUsers(
        response?.data,
        currentUserId,
        includeCurrentUser
    );

    if (!normalized) {
        throw new Error('Users search response is invalid.');
    }

    return {
        users: normalized,
        nextToken: response?.headers?.['pagination-token'] || null,
        totalCount: Number(response?.headers?.['pagination-count'] || 0)
    };
};

export const pingCallableUsers = async ({ userRoleUuid } = {}) => {
    if (callConfig.mode === 'local-dev') {
        return { ok: true };
    }

    await api.get('/calls/ping', {
        headers: makeRoleHeaders(userRoleUuid)
    });

    return { ok: true };
};
